'use client';

import { useState, useCallback, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface PdfViewerProps {
  /**
   * Stable Uint8Array reference (a new one per loaded/regenerated document).
   *
   * IMPORTANT: the parent must NOT re-create this Uint8Array on every render.
   * Store it in state (useState / useRef) so the reference stays stable between
   * renders — PdfViewer's useMemo/useId key off reference equality to detect
   * a genuinely new document vs. an unrelated re-render.
   */
  pdfBytes: Uint8Array;
  maxWidth?: number;
}

export function PdfViewer({ pdfBytes, maxWidth = 860 }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNum, setPageNum]   = useState<number>(1);
  const [scale, setScale]       = useState<number>(1.0);
  const [loadErr, setLoadErr]   = useState<string | null>(null);

  // react-pdf's <Document> keeps a ref to the previous `file` prop and
  // deep-compares it (dequal) against the new one on every change — but
  // once the PDF.js worker has consumed a document's bytes (via
  // postMessage), that ArrayBuffer is detached, and dequal reading it on
  // the next "Regenerate" throws "Cannot perform Construct on a detached
  // ArrayBuffer" (react-pdf issues #1539/#1657 — never fixed upstream, no
  // config exists to disable the worker transfer). Two independent
  // mitigations, per the maintainer's own recommended workaround:
  //  1. A Blob (not a raw ArrayBuffer) is never transferable/detachable.
  //  2. A `key` on <Document> tied to this pdfBytes forces a full remount
  //     on regenerate, so there's never a stale prevFile.current to diff.
  const versionRef = useRef(0);
  const seenBytesRef = useRef<Uint8Array | null>(null);
  if (seenBytesRef.current !== pdfBytes) {
    seenBytesRef.current = pdfBytes;
    versionRef.current += 1;
  }
  const documentKey = versionRef.current;

  const pdfFile = useMemo(
    () => new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' }),
    [pdfBytes],
  );

  const onLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setPageNum(1);
    setLoadErr(null);
  }, []);

  const onLoadError = useCallback((err: Error) => {
    setLoadErr(err.message);
  }, []);

  // Only ctrl/cmd+wheel (pinch-zoom on trackpads is reported this way, as is
  // ctrl+scroll on a mouse) adjusts zoom; a plain wheel/two-finger scroll is
  // left to the container's native scroll so it never escapes to a sibling
  // element (e.g. the OCR panel) or the page.
  const onWheel = useCallback((e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.25 : -0.25;
    setScale(s => Math.min(5, Math.max(0.5, parseFloat((s + delta).toFixed(2)))));
  }, []);

  if (loadErr) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 py-16 text-center px-6">
        <svg className="h-10 w-10 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-red-500">Could not render PDF: {loadErr}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {numPages > 0 && (
        <div className="flex items-center justify-between border-b border-gray-100 bg-white px-4 py-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPageNum(p => Math.max(1, p - 1))}
              disabled={pageNum <= 1}
              className="rounded-md border border-gray-300 p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
              aria-label="Previous page"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-xs text-gray-600 min-w-[80px] text-center">
              Page {pageNum} / {numPages}
            </span>
            <button
              onClick={() => setPageNum(p => Math.min(numPages, p + 1))}
              disabled={pageNum >= numPages}
              className="rounded-md border border-gray-300 p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-40"
              aria-label="Next page"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setScale(s => Math.max(0.5, parseFloat((s - 0.25).toFixed(2))))}
              disabled={scale <= 0.5}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
            >−</button>
            <span className="text-xs text-gray-600 min-w-[44px] text-center">{Math.round(scale * 100)}%</span>
            <button
              onClick={() => setScale(s => Math.min(5, parseFloat((s + 0.25).toFixed(2))))}
              disabled={scale >= 5}
              className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 disabled:opacity-40"
            >+</button>
            <button
              onClick={() => setScale(1)}
              className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
            >Reset</button>
          </div>
        </div>
      )}

      <div
        className="flex-1 overflow-auto bg-gray-200 py-4"
        style={{ minHeight: 0 }}
        onWheel={onWheel}
      >
        <Document
          key={documentKey}
          file={pdfFile}
          onLoadSuccess={onLoadSuccess}
          onLoadError={onLoadError}
          className="mx-auto w-fit"
          loading={
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
              <p className="text-sm text-gray-500">Rendering PDF…</p>
            </div>
          }
          error={
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <p className="text-sm text-red-500">Failed to render this PDF.</p>
            </div>
          }
        >
          <Page
            pageNumber={pageNum}
            scale={scale}
            width={maxWidth}
            renderTextLayer
            renderAnnotationLayer
            className="shadow-lg"
            loading={
              <div className="flex items-center justify-center h-32">
                <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" />
              </div>
            }
          />
        </Document>
      </div>
    </div>
  );
}
